import { readVarM } from '@execonline-inc/environment';
import { toResult } from '@execonline-inc/maybe-adapter';
import { noop, pipe } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { Task } from 'taskarian';
import { missingValue } from '../../../Analytics/Types';
import { errorMessage } from '../../../ExceptionHandling';
import {
  AnalyticsEvent,
  DataLayer,
  GtmDataLayer,
  GtmLoadError,
  gtmLoadError,
  handleProgramUpdateError,
} from './Types';

declare global {
  export interface Window {
    dataLayer?: DataLayer[];
    ga?: (eventType: string, fieldsObject: AnalyticsEvent) => void;
  }
}

// Google Tag Manager reads window.dataLayer as a plain array. Besides our own
// DataLayer records it accepts its own bootstrap event, consent calls (an
// `arguments` object) and reset functions, so pushes go through this
// untyped view of the same array. Nothing here injects inline <script> text,
// which keeps the page compatible with a CSP that has no 'unsafe-inline'.
const rawDataLayer = (): unknown[] => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer as unknown[];
};

// gtag() pushes its `arguments` object, not an array. GTM's consent handling
// depends on that shape.
function gtag(..._args: unknown[]): void {
  rawDataLayer().push(arguments);
}

const setDefaultConsent = (): void => {
  gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
  });
};

// The standard GTM snippet, as a function instead of inline script text.
const loadGtmContainer = (token: string): void => {
  rawDataLayer().push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(token)}`;
  const first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
};

export const loadGoogleTagManager = (
  token: string,
  newToken: string,
): Task<GtmLoadError, GtmDataLayer> => {
  return new Task<GtmLoadError, GtmDataLayer>((reject, resolve) => {
    const rejectException = pipe(errorMessage, gtmLoadError, reject);
    try {
      if (window.dataLayer) {
        resolve({ dataLayer: window.dataLayer });
      } else {
        // No GTM <noscript> iframe: this app cannot run without JavaScript,
        // and an iframe built with the DOM API loads even inside <noscript>,
        // which the CSP frame-src then blocks and reports.
        if (document.body) {
          setDefaultConsent();
          loadGtmContainer(token);
          loadGtmContainer(newToken);
          if (window.dataLayer) {
            resolve({ dataLayer: window.dataLayer });
          } else {
            reject(gtmLoadError('window.dataLayer not loaded by script'));
          }
        }
      }
    } catch (e) {
      rejectException(e);
    }

    return noop;
  });
};

// GTM used to drop a repeat push for the same page with a Custom HTML tag and
// a Custom JavaScript variable (previousPageTitle). Those need 'unsafe-inline'
// and 'unsafe-eval' in the CSP, so the repeat check lives here instead: true
// the first time an event is pushed with a key, false for a repeat of the
// last key for that event.
const lastKeyByEvent = new Map<string, string>();

export const firstPushFor = (event: string, key: string): boolean => {
  if (lastKeyByEvent.get(event) === key) {
    return false;
  }
  lastKeyByEvent.set(event, key);
  return true;
};

export const dataLayerUpdate = (dataLayer: DataLayer) => {
  setTimeout(() => {
    rawDataLayer().push(dataLayer);
  });
};

export const dataLayer = (dataLayer: DataLayer) => {
  const layer = rawDataLayer();
  layer.push(function (this: { reset: () => void }) {
    this.reset();
  });
  layer.push(dataLayer);
};

export const updateGtmProgramFields = (
  title: string,
  sfProgramUid: Maybe<string>,
  registrationType: Maybe<string>,
): void => {
  toResult(
    missingValue('sfProgramUid'),
    sfProgramUid.map((sfProgramUid) => ({ sfProgramUid })),
  )
    .assign('registrationType', toResult(missingValue('registrationType'), registrationType))
    .assign('token', toResult(missingValue('token'), readVarM('VITE_GTM_KEY')))
    .assign('newToken', toResult(missingValue('newToken'), readVarM('VITE_NEW_GTM_KEY')))
    .map(({ sfProgramUid, registrationType, token }) => {
      dataLayerUpdate({
        course: `${title}: ${sfProgramUid}`,
        userType: registrationType,
      });
    })
    .mapError(handleProgramUpdateError(title));
};

export const triggerAnalyticsEvent = (
  hitType: string,
  eventCategory: string,
  eventAction: string,
  eventLabel: string,
) => {
  if (window.ga) {
    window.ga('send', {
      hitType,
      eventCategory,
      eventAction,
      eventLabel,
    });
  }
};
